import React, { useState, useEffect, useCallback } from 'react';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import { useToast } from '../../context/ToastContext';
import {
  getReviewOverview,
  getReviewQueue,
  getReviewDetail,
  approveRun,
  rejectRun,
  decideCompetition,
  getSwapQueue,
  decideSwap,
  searchReviewListings,
  addListingToRun,
} from '../../services/compMatcherService';
import styles from './PocOwnerReviewQueue.module.css';

/**
 * The review queue — Slice 7 of COMP_MATCHER_MIGRATION_PLAN.md.
 *
 * Every set of matches passes through here before a family sees it. Approving
 * releases the payload to the student; rejecting keeps it withheld and, by
 * default, re-runs the match with the reviewer's note fed in as a correction —
 * regenerating without the note reliably produces the same list and the same
 * rejection.
 *
 * The retrieval trace is shown on purpose. "The model ranked badly" and
 * "eligibility dropped everything before the model saw it" look identical from
 * the list alone, and the fixes are completely different.
 */
const PocOwnerReviewQueue = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [queue, setQueue] = useState([]);
  const [swaps, setSwaps] = useState([]);
  const [openRun, setOpenRun] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [o, q, sw] = await Promise.all([
        getReviewOverview(),
        getReviewQueue(),
        getSwapQueue(),
      ]);
      setOverview(o);
      setQueue(q.reviews || []);
      setSwaps(sw.requests || []);
    } catch (error) {
      toast.error('Could not load the review queue: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const open = async (runId) => {
    try {
      setOpenRun(await getReviewDetail(runId));
      setNote('');
    } catch (error) {
      toast.error('Could not open that run: ' + error.message);
    }
  };

  const [deciding, setDeciding] = useState(null);

  // Tick or cross one competition. The student only ever sees ticked ones, so
  // a cross removes it from their report rather than ranking it lower.
  const decideOne = async (item, approved) => {
    setDeciding(item.item_id);
    try {
      const result = await decideCompetition(openRun.id, item.item_id, approved);
      if (result.remaining === 0) {
        toast.success(
          result.approved_count > 0
            ? `All decided — ${result.approved_count} competition(s) released to the student.`
            : 'All competitions were rejected; the run goes back for a re-run.'
        );
        setOpenRun(null);
        await load();
      } else {
        toast.success(`${approved ? 'Approved' : 'Rejected'} — ${result.remaining} left to decide.`);
        setOpenRun(await getReviewDetail(openRun.id));
      }
    } catch (error) {
      toast.error('Could not record that: ' + error.message);
    } finally {
      setDeciding(null);
    }
  };

  // A swap a student asked for goes through the same gate as anything the
  // matcher proposed — approving it ticks the new competition in and crosses
  // the old one out in one step.
  const resolveSwap = async (request, approved) => {
    try {
      await decideSwap(request.id, approved);
      toast.success(
        approved
          ? `${request.to_listing?.name} approved for ${request.student_name}.`
          : 'Request declined.'
      );
      await load();
    } catch (error) {
      toast.error('Could not record that: ' + error.message);
    }
  };

  const decide = async (approve) => {
    setBusy(true);
    try {
      if (approve) {
        await approveRun(openRun.id, note || null);
        toast.success('Approved — the student can see these matches now.');
      } else {
        const result = await rejectRun(openRun.id, note || null, true);
        toast.success(
          result.regenerating
            ? 'Rejected. A new set is being generated with your note.'
            : `Rejected. Not regenerated: ${result.reason || 'unknown reason'}`
        );
      }
      setOpenRun(null);
      await load();
    } catch (error) {
      toast.error('That did not go through: ' + error.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <LoadingSpinner size="large" message="Loading review queue..." />
      </div>
    );
  }

  const items = openRun?.payload?.items || [];
  const trace = openRun?.retrieval_trace || {};

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Competition Review</h1>
        <p className={styles.subtitle}>
          Nothing reaches a family until it is approved here.
        </p>
      </div>

      {overview && (
        <Card>
          <div className={styles.stats}>
            <Stat label="Awaiting review" value={overview.pending_reviews} highlight />
            <Stat label="Approved" value={overview.approved_runs} />
            <Stat label="Matcher enabled" value={overview.matcher_enabled} />
            <Stat label="Listings (current)" value={`${overview.listings.current} / ${overview.listings.total}`} />
          </div>
          {overview.warning && <p className={styles.warning}>⚠ {overview.warning}</p>}
        </Card>
      )}

      {swaps.length > 0 && !openRun && (
        <Card>
          <h3 className={styles.stateHeading}>
            {swaps.length} competition change request{swaps.length === 1 ? '' : 's'}
          </h3>
          {swaps.map((request) => (
            <div key={request.id} className={styles.queueRow}>
              <div>
                <div className={styles.studentName}>{request.student_name || 'Student'}</div>
                <div className={styles.queueMeta}>
                  wants <strong>{request.to_listing?.name}</strong>
                  {request.from_listing ? ` instead of ${request.from_listing.name}` : ' as an extra'}
                </div>
                {request.student_reason && (
                  <div className={styles.queueMeta}>“{request.student_reason}”</div>
                )}
              </div>
              <div className={styles.itemActions}>
                <button
                  className={styles.tick}
                  onClick={() => resolveSwap(request, true)}
                  title="Approve this swap"
                >
                  ✓
                </button>
                <button
                  className={styles.cross}
                  onClick={() => resolveSwap(request, false)}
                  title="Decline this swap"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </Card>
      )}

      {!openRun ? (
        <Card>
          {queue.length === 0 ? (
            <p className={styles.empty}>Nothing is waiting for review.</p>
          ) : (
            queue.map((run) => (
              <div key={run.id} className={styles.queueRow}>
                <div>
                  <div className={styles.studentName}>{run.student_name || 'Unnamed student'}</div>
                  <div className={styles.queueMeta}>
                    {run.item_count} competition{run.item_count === 1 ? '' : 's'}
                    {run.grade ? ` · Grade ${run.grade}` : ''}
                    {run.school ? ` · ${run.school}` : ''}
                  </div>
                </div>
                <Button size="small" variant="outline" onClick={() => open(run.id)}>
                  Review
                </Button>
              </div>
            ))
          )}
        </Card>
      ) : (
        <Card>
          <button className={styles.back} onClick={() => setOpenRun(null)}>
            ← Back to queue
          </button>

          {(openRun.student || openRun.project) && (
            <div className={styles.contextBox}>
              <div className={styles.contextRow}>
                <strong>{`${openRun.student?.first_name || ''} ${openRun.student?.last_name || ''}`.trim() || 'Student'}</strong>
                {openRun.student?.program_enrolled && (
                  <span className={styles.chip}>{openRun.student.program_enrolled}</span>
                )}
                {openRun.student?.grade && <span className={styles.chip}>Grade {openRun.student.grade}</span>}
                {openRun.student?.school && <span className={styles.contextMeta}>{openRun.student.school}</span>}
              </div>
              {openRun.project?.name && (
                <div className={styles.contextProject}>
                  <div className={styles.contextRow}>
                    <strong>{openRun.project.name}</strong>
                    {openRun.project.domain && <span className={styles.chip}>{openRun.project.domain}</span>}
                  </div>
                  {openRun.project.description && (
                    <p className={styles.contextDesc}>{openRun.project.description}</p>
                  )}
                </div>
              )}
            </div>
          )}

          <h3 className={styles.stateHeading}>
            {items.length} competition{items.length === 1 ? '' : 's'} proposed
          </h3>

          {items.map((item) => (
            <div key={item.rank} className={styles.result}>
              <div className={styles.resultHead}>
                <span className={styles.compName}>
                  {item.rank}. {(item.listing || item.competition || {}).name}
                  {item.added_manually && <span className={styles.manualTag}>added by team</span>}
                </span>
                <div className={styles.itemActions}>
                  {item.score != null && (
                    <span className={styles.score}>
                      {item.fit_bucket ? `${item.fit_bucket} · ` : ''}{Math.round(item.score)}
                    </span>
                  )}
                  {item.review_state && item.review_state !== 'PENDING' ? (
                    <span
                      className={
                        item.review_state === 'APPROVED' ? styles.stateApproved : styles.stateRejected
                      }
                    >
                      {item.review_state === 'APPROVED' ? '✓ Approved' : '✕ Rejected'}
                    </span>
                  ) : (
                    <>
                      <button
                        className={styles.tick}
                        disabled={deciding === item.item_id}
                        onClick={() => decideOne(item, true)}
                        title="Approve this competition"
                      >
                        ✓
                      </button>
                      <button
                        className={styles.cross}
                        disabled={deciding === item.item_id}
                        onClick={() => decideOne(item, false)}
                        title="Reject this competition"
                      >
                        ✕
                      </button>
                    </>
                  )}
                </div>
              </div>
              <p className={styles.reason}>{item.reason}</p>
              {(() => {
                // The live competition_listings row, joined on read — so this is
                // current detail rather than what was frozen at generation time.
                const l = item.listing || item.competition || {};
                const urls = Array.isArray(l.official_urls) ? l.official_urls : [];
                const facts = [
                  l.region && ['Region', l.region],
                  l.primary_domain && ['Domain', l.primary_domain],
                  l.submission_type && ['Submission', l.submission_type],
                  (l.deadline_text || l.deadline_date) && [
                    'Deadline', l.is_rolling ? 'Rolling' : (l.deadline_text || l.deadline_date),
                  ],
                  l.registration_status && ['Registration', l.registration_status],
                  l.eligibility_raw && ['Eligibility', l.eligibility_raw],
                  l.team_raw && ['Team', l.team_raw],
                  (l.prestige || l.selectivity || l.difficulty) && [
                    'Rating',
                    [l.prestige && `prestige ${l.prestige}`, l.selectivity && `selectivity ${l.selectivity}`, l.difficulty]
                      .filter(Boolean).join(' · '),
                  ],
                ].filter(Boolean);
                if (!l.description && !urls.length && !facts.length) return null;
                return (
                  <details className={styles.compDetails}>
                    <summary>Competition details</summary>
                    <div className={styles.compBody}>
                      {l.description && <p className={styles.compDesc}>{l.description}</p>}
                      {l.submission_details && (
                        <p className={styles.compDesc}><strong>What to submit: </strong>{l.submission_details}</p>
                      )}
                      {facts.length > 0 && (
                        <dl className={styles.factGrid}>
                          {facts.map(([k, v]) => (
                            <React.Fragment key={k}>
                              <dt>{k}</dt><dd>{v}</dd>
                            </React.Fragment>
                          ))}
                        </dl>
                      )}
                      {urls.length > 0 && (
                        <div className={styles.compLinks}>
                          {urls.map((u) => (
                            <a key={u} href={u} target="_blank" rel="noopener noreferrer">{u}</a>
                          ))}
                        </div>
                      )}
                      {l.notes && <p className={styles.compNote}><strong>Internal note: </strong>{l.notes}</p>}
                      {Array.isArray(l.warnings) && l.warnings.length > 0 && (
                        <ul className={styles.compWarnings}>
                          {l.warnings.map((w) => <li key={w}>{w}</li>)}
                        </ul>
                      )}
                    </div>
                  </details>
                );
              })()}
            </div>
          ))}

          <AddCompetition
            runId={openRun.id}
            onAdded={() => open(openRun.id)}
            heldIds={items.map((i) => (i.listing || i.competition || {}).id).filter(Boolean)}
          />

          {openRun.payload?.reviewer_notes && (
            <p className={styles.modelNote}>
              <strong>Model note:</strong> {openRun.payload.reviewer_notes}
            </p>
          )}

          <details className={styles.trace}>
            <summary>Retrieval trace</summary>
            <div className={styles.traceBody}>
              <div>query: {trace.query}</div>
              <div>region: {String(trace.region)}</div>
              <div>
                retrieved {trace.retrieved} → {trace.survived_filters} passed region/current →{' '}
                {trace.eligible_count} eligible
              </div>
              {trace.diagnosis && <div className={styles.warning}>{trace.diagnosis}</div>}
              {trace.hallucinated_ids?.length > 0 && (
                <div className={styles.warning}>
                  invented ids discarded: {trace.hallucinated_ids.join(', ')}
                </div>
              )}
              {trace.dropped_by_hard_filter?.length > 0 && (
                <div>
                  dropped by eligibility:{' '}
                  {trace.dropped_by_hard_filter
                    .map((d) => `${d.name} (${d.rejections?.[0]?.code})`)
                    .join(', ')}
                </div>
              )}
            </div>
          </details>

          <label className={styles.noteLabel}>
            Note
            <textarea
              className={styles.noteInput}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="On a rejection this is fed back to the matcher as a correction — say what was wrong with the set."
              rows={3}
            />
          </label>

          <div className={styles.actions}>
            <Button onClick={() => decide(true)} loading={busy}>
              Approve &amp; release
            </Button>
            <Button variant="outline" onClick={() => decide(false)} loading={busy}>
              Reject &amp; regenerate
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

const Stat = ({ label, value, highlight }) => (
  <div className={styles.stat}>
    <div className={highlight ? styles.statValueHighlight : styles.statValue}>{value}</div>
    <div className={styles.statLabel}>{label}</div>
  </div>
);

/**
 * Add a competition the matcher never proposed.
 *
 * Picks from the existing repository rather than offering a create-a-listing
 * form: a hand-written listing would not be in Chroma, so it could never match
 * organically for anyone else, and the reviewer would not know that.
 */
const AddCompetition = ({ runId, onAdded, heldIds = [] }) => {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(null);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    // Debounced: the picker searches as you type and this hits the database.
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        setResults(await searchReviewListings(term, 25));
      } catch (e) {
        toast.error(e.response?.data?.detail || e.message || 'Search failed.');
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, open]);

  const add = async (listing) => {
    setAdding(listing.id);
    try {
      await addListingToRun(runId, listing.id, note.trim() || null);
      toast.success(`${listing.name} added to this report.`);
      setOpen(false);
      setTerm('');
      setNote('');
      onAdded?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Could not add that competition.');
    } finally {
      setAdding(null);
    }
  };

  if (!open) {
    return (
      <button className={styles.addToggle} onClick={() => setOpen(true)}>
        + Add a competition manually
      </button>
    );
  }

  return (
    <div className={styles.addBox}>
      <div className={styles.addHead}>
        <strong>Add a competition</strong>
        <button className={styles.addClose} onClick={() => setOpen(false)}>✕</button>
      </div>
      <input
        className={styles.addSearch}
        autoFocus
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder="Search current competitions by name…"
      />
      <input
        className={styles.addSearch}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Why this one? (optional, kept on the record)"
      />
      <p className={styles.addHint}>
        Added competitions are approved immediately — picking one by hand is the decision.
      </p>
      {searching ? (
        <p className={styles.addHint}>Searching…</p>
      ) : results.length === 0 ? (
        <p className={styles.addHint}>No current competitions match that.</p>
      ) : (
        <ul className={styles.addList}>
          {results.map((l) => {
            const held = heldIds.includes(l.id);
            return (
              <li key={l.id}>
                <div>
                  <div className={styles.addName}>{l.name}</div>
                  <div className={styles.addMeta}>
                    {[l.region, l.primary_domain, l.is_rolling ? 'Rolling' : (l.deadline_text || l.deadline_date)]
                      .filter(Boolean).join(' · ')}
                  </div>
                </div>
                <button
                  className={styles.addBtn}
                  disabled={held || adding === l.id}
                  onClick={() => add(l)}
                >
                  {held ? 'on report' : adding === l.id ? 'adding…' : 'Add'}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default PocOwnerReviewQueue;
